import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useHR } from "../HRContext";
import {
  HREmployee,
  EmployeeFormData,
  HRDepartment,
  HRPosition,
} from "../../types/hr";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
  Button,
  Divider,
  message,
  Spin,
  Upload,
  Avatar,
} from "antd";
import { UserOutlined, CameraOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import API_CONFIG from '../../config/api.js';

const { Option } = Select;
const { TextArea } = Input;

interface EmployeeFormProps {
  employee?: HREmployee | null;
  onClose: () => void;
}

const EmployeeForm: React.FC<EmployeeFormProps> = ({ employee, onClose }) => {
  const { departments, positions, employees, createEmployee, updateEmployee } =
    useHR();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const existingPhotoUrlRef = useRef<string | null>(null);

  const clearExistingPreview = () => {
    if (existingPhotoUrlRef.current) {
      URL.revokeObjectURL(existingPhotoUrlRef.current);
      existingPhotoUrlRef.current = null;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadExistingPhoto = async (employeeId: number) => {
      clearExistingPreview();
      try {
        const response = await axios.get(
          API_CONFIG.apiUrl(`/hr/employees/${employeeId}/photo`),
          { responseType: "blob" }
        );
        const objectUrl = window.URL.createObjectURL(response.data);
        existingPhotoUrlRef.current = objectUrl;
        if (isMounted) {
          setPhotoPreview(objectUrl);
        } else {
          window.URL.revokeObjectURL(objectUrl);
        }
      } catch (error) {
        if (isMounted) {
          setPhotoPreview(null);
        }
      }
    };
    if (employee) {
      form.setFieldsValue({
        employee_number: employee.employee_number || "",
        first_name: employee.first_name || "",
        last_name: employee.last_name || "",
        email: employee.email || "",
        phone: employee.phone || "",
        date_of_birth: employee.date_of_birth
          ? dayjs(employee.date_of_birth)
          : undefined,
        place_of_birth: employee.place_of_birth || "",
        nationality: employee.nationality || "Burkinabè",
        gender: employee.gender || undefined,
        marital_status: employee.marital_status || undefined,
        address: employee.address || "",
        city: employee.city || "",
        postal_code: employee.postal_code || "",
        country: employee.country || "Burkina Faso",
        national_id: employee.national_id || "",
        passport_number: employee.passport_number || "",
        driving_license: employee.driving_license || "",
        social_security_number: employee.social_security_number || "",
        tax_number: employee.tax_number || "",
        bank_name: employee.bank_name || "",
        bank_account: employee.bank_account || "",
        bank_branch: employee.bank_branch || "",
        hire_date: employee.hire_date ? dayjs(employee.hire_date) : undefined,
        confirmation_date: employee.confirmation_date
          ? dayjs(employee.confirmation_date)
          : undefined,
        department_id: employee.department_id || undefined,
        position_id: employee.position_id || undefined,
        manager_id: employee.manager_id || undefined,
        status: employee.status || "active",
        professional_category: employee.professional_category || "non_cadre",
        dependents_count: employee.dependents_count || 0,
        emergency_contact_name: employee.emergency_contact_name || "",
        emergency_contact_phone: employee.emergency_contact_phone || "",
        emergency_contact_relationship:
          employee.emergency_contact_relationship || "",
        notes: employee.notes || "",
      });

      // Charger la photo existante si elle existe
      if (employee.photo && employee.id) {
        loadExistingPhoto(employee.id);
      } else {
        clearExistingPreview();
        setPhotoPreview(null);
      }
    } else {
      form.resetFields();
      setPhotoFile(null);
      clearExistingPreview();
      setPhotoPreview(null);
    }

    return () => {
      isMounted = false;
      clearExistingPreview();
    };
  }, [employee, form]);

  const handleSubmit = async (values: any) => {
    setLoading(true);

    try {
      // --- LOG DE DÉBOGAGE ---
      console.log("[EmployeeForm] Valeurs brutes du formulaire:", values);
      // --- FIN LOG ---

      const formData: EmployeeFormData = {
        ...values,
        date_of_birth: values.date_of_birth
          ? values.date_of_birth.format("YYYY-MM-DD")
          : undefined,
        hire_date: values.hire_date
          ? values.hire_date.format("YYYY-MM-DD")
          : "",
        confirmation_date: values.confirmation_date
          ? values.confirmation_date.format("YYYY-MM-DD")
          : undefined,
        nationality: values.nationality || "Burkinabè",
        country: values.country || "Burkina Faso",
        // Ajouter la photo si un fichier a été sélectionné
        ...(photoFile && { photo: photoFile }),
      };

      // --- LOG DE DÉBOGAGE ---
      console.log("[EmployeeForm] Données formatées pour l'API:", formData);
      // --- FIN LOG ---

      if (employee) {
        await updateEmployee(employee.id, formData);
        message.success("Employé modifié avec succès");
      } else {
        await createEmployee(formData);
        message.success("Employé créé avec succès");
      }
      onClose();
    } catch (error) {
      message.error("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  // Gestion de l'upload de photo
  const handlePhotoChange = (info: any) => {
    const { file, fileList } = info;

    if (file.status === "removed") {
      setPhotoFile(null);
      clearExistingPreview();
      setPhotoPreview(null);
      return;
    }

    // Validation du fichier
    const isImage = file.type?.startsWith("image/");
    if (!isImage) {
      message.error("Vous ne pouvez télécharger que des fichiers image !");
      return;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("L'image doit être inférieure à 2MB !");
      return;
    }

    // Créer une prévisualisation
    clearExistingPreview();
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Stocker le fichier
    setPhotoFile(file);
  };

  const uploadButton = (
    <div style={{ textAlign: "center" }}>
      <CameraOutlined style={{ fontSize: "24px", color: "#999" }} />
      <div style={{ marginTop: 8, color: "#999" }}>
        {photoPreview ? "Changer la photo" : "Ajouter une photo"}
      </div>
    </div>
  );

  return (
    <Modal
      title={employee ? "Modifier l'employé" : "Nouvel employé"}
      open={true}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: "active",
          }}
        >
          <Row gutter={[24, 0]}>
            {/* Informations personnelles */}
            <Col xs={24} md={12}>
              <Divider orientation="left">Informations personnelles</Divider>

              <Form.Item
                name="employee_number"
                label="Numéro d'employé"
                rules={[
                  {
                    required: true,
                    message: "Veuillez saisir le numéro d'employé",
                  },
                ]}
              >
                <Input placeholder="Numéro d'employé" />
              </Form.Item>

              {/* Photo de profil */}
              <Form.Item label="Photo de profil">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "16px" }}
                >
                  <Avatar
                    size={80}
                    icon={<UserOutlined />}
                    src={photoPreview}
                    style={{
                      border: "2px solid #f0f0f0",
                      backgroundColor: photoPreview ? "transparent" : "#fafafa",
                    }}
                  />
                  <Upload
                    name="photo"
                    listType="picture-card"
                    showUploadList={false}
                    beforeUpload={() => false} // Empêcher l'upload automatique
                    onChange={handlePhotoChange}
                    style={{ border: "none" }}
                  >
                    {uploadButton}
                  </Upload>
                  {photoPreview && (
                    <Button
                      type="text"
                      danger
                      size="small"
                      onClick={() => {
                        setPhotoFile(null);
                        clearExistingPreview();
                        setPhotoPreview(null);
                      }}
                    >
                      Supprimer
                    </Button>
                  )}
                </div>
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="first_name"
                    label="Prénom"
                    rules={[
                      { required: true, message: "Veuillez saisir le prénom" },
                    ]}
                  >
                    <Input placeholder="Prénom" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="last_name"
                    label="Nom"
                    rules={[
                      { required: true, message: "Veuillez saisir le nom" },
                    ]}
                  >
                    <Input placeholder="Nom" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { type: "email", message: "Veuillez saisir un email valide" },
                ]}
              >
                <Input placeholder="Email" />
              </Form.Item>

              <Form.Item name="phone" label="Téléphone">
                <Input placeholder="Téléphone" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="date_of_birth" label="Date de naissance">
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD/MM/YYYY"
                      placeholder="Date de naissance"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="place_of_birth" label="Lieu de naissance">
                    <Input placeholder="Lieu de naissance" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="gender"
                    label="Genre"
                    rules={[
                      {
                        required: true,
                        message: "Veuillez sélectionner le genre",
                      },
                    ]}
                  >
                    <Select placeholder="Sélectionner le genre">
                      <Option value="M">Masculin</Option>
                      <Option value="F">Féminin</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="marital_status"
                    label="Situation matrimoniale"
                  >
                    <Select
                      placeholder="Sélectionner la situation matrimoniale"
                      allowClear
                    >
                      <Option value="single">Célibataire</Option>
                      <Option value="married">Marié(e)</Option>
                      <Option value="divorced">Divorcé(e)</Option>
                      <Option value="widowed">Veuf(ve)</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="professional_category"
                    label="Catégorie professionnelle"
                    tooltip="Détermine le taux d'abattement forfaitaire: Cadre (20%) ou Non-cadre (25%)"
                    rules={[
                      {
                        required: true,
                        message:
                          "Veuillez sélectionner la catégorie professionnelle",
                      },
                    ]}
                  >
                    <Select placeholder="Sélectionner la catégorie">
                      <Option value="non_cadre">
                        Non-cadre (25% d'abattement)
                      </Option>
                      <Option value="cadre">Cadre (20% d'abattement)</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="dependents_count"
                    label="Nombre de charges"
                    tooltip="Nombre de personnes à charge pour la réduction IUTS (0 à 7+)"
                    rules={[
                      {
                        required: true,
                        message: "Veuillez saisir le nombre de charges",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      max={10}
                      placeholder="Nombre de charges"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="nationality" label="Nationalité">
                    <Input placeholder="Nationalité" defaultValue="Burkinabè" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="country" label="Pays">
                    <Input placeholder="Pays" defaultValue="Burkina Faso" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="address" label="Adresse">
                <TextArea rows={2} placeholder="Adresse complète" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="city" label="Ville">
                    <Input placeholder="Ville" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="postal_code" label="Code postal">
                    <Input placeholder="Code postal" />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            {/* Informations professionnelles */}
            <Col xs={24} md={12}>
              <Divider orientation="left">
                Informations professionnelles
              </Divider>

              <Form.Item
                name="hire_date"
                label="Date d'embauche"
                rules={[
                  {
                    required: true,
                    message: "Veuillez sélectionner la date d'embauche",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Date d'embauche"
                />
              </Form.Item>

              <Form.Item name="department_id" label="Département">
                <Select placeholder="Sélectionner un département" allowClear>
                  {departments.map((dept: HRDepartment) => (
                    <Option key={dept.id} value={dept.id}>
                      {dept.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="position_id" label="Poste">
                <Select placeholder="Sélectionner un poste" allowClear>
                  {positions.map((pos: HRPosition) => (
                    <Option key={pos.id} value={pos.id}>
                      {pos.title}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="manager_id" label="Manager">
                <Select placeholder="Sélectionner un manager" allowClear>
                  {employees
                    .filter((emp) => emp.id !== employee?.id)
                    .map((emp: HREmployee) => (
                      <Option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item name="confirmation_date" label="Date de confirmation">
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Date de confirmation"
                />
              </Form.Item>

              <Form.Item
                name="status"
                label="Statut"
                rules={[
                  {
                    required: true,
                    message: "Veuillez sélectionner le statut",
                  },
                ]}
              >
                <Select placeholder="Sélectionner le statut">
                  <Option value="active">Actif</Option>
                  <Option value="inactive">Inactif</Option>
                  <Option value="suspended">Suspendu</Option>
                  <Option value="terminated">Terminé</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Informations additionnelles */}
          <Divider orientation="left">Informations additionnelles</Divider>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="emergency_contact_name"
                label="Contact d'urgence (nom)"
              >
                <Input placeholder="Nom du contact d'urgence" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="emergency_contact_phone"
                label="Contact d'urgence (téléphone)"
              >
                <Input placeholder="Téléphone du contact d'urgence" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="emergency_contact_relationship" label="Relation">
                <Input placeholder="Relation avec le contact d'urgence" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="national_id" label="Numéro CNI">
                <Input placeholder="Numéro de carte nationale d'identité" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="passport_number" label="Numéro de passeport">
                <Input placeholder="Numéro de passeport" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="driving_license" label="Permis de conduire">
                <Input placeholder="Numéro de permis de conduire" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="social_security_number" label="Numéro CNSS">
                <Input placeholder="Numéro de sécurité sociale" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="tax_number" label="Numéro fiscal">
                <Input placeholder="Numéro d'identification fiscale" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="bank_name" label="Banque">
                <Input placeholder="Nom de la banque" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="bank_account" label="Numéro de compte">
                <Input placeholder="Numéro de compte bancaire" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="bank_branch" label="Agence">
                <Input placeholder="Agence bancaire" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <Form.Item name="notes" label="Notes">
                <TextArea rows={3} placeholder="Notes additionnelles" />
              </Form.Item>
            </Col>
          </Row>

          {/* Boutons */}
          <Divider />

          <Row justify="end" gutter={16}>
            <Col>
              <Button onClick={onClose}>Annuler</Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit" loading={loading}>
                {employee ? "Modifier" : "Créer"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EmployeeForm;
